import axios from 'axios';
import uniqid from 'uniqid';
import { jwtDecode } from "jwt-decode";
import { Link, useLocation } from 'react-router-dom';
import { useNavigate } from "react-router-dom";
import React, { useState, useEffect, useRef, useMemo } from 'react';
import { ToastContainer, toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css'; // Estilos de react-toastify
import {
  FiUser, FiPhone, FiMail, FiGlobe, FiUploadCloud, FiFile,
  FiArrowRight, FiCode, FiPenTool, FiTrendingUp, FiShare2,
} from 'react-icons/fi';
import BASE_URL from '../../config/config';
import Verificacion from '../../components/Verificacion';
import Suspendido from '../../components/Suspendido';
import Loader from '../../components/Loader';
import RichTextEditor from '../../components/editor/RichTextEditor';
import { esHtmlVacio, sanitizarHtml } from '../../components/editor/sanitizarHtml';
import '../../css/nuevo-ticket.css';

const DEPARTAMENTOS = [
  {
    value: 'desarrollo',
    label: 'Desarrollo web',
    icon: FiCode,
    descripcion: 'Si tu tarea/incidencia está relacionada con tu página web, software a medida, correo electrónico, dominios y hosting.',
  },
  {
    value: 'diseno',
    label: 'Diseño gráfico',
    icon: FiPenTool,
    descripcion: 'Si tu tarea/incidencia está relacionada con branding, diseño editorial, diseño para eventos, papelería, rotulación, material promocional, producción de video corporativo/publicitario, edición fotográfica/postproducción de video y fotografía corporativa/producto.',
  },
  {
    value: 'marketing',
    label: 'Marketing',
    icon: FiTrendingUp,
    descripcion: 'Si tu tarea/incidencia está relacionada con SEO, campañas de pago (SEM + SMM), campañas de email marketing o informes mensuales de analítica web.',
  },
  {
    value: 'redes_sociales',
    label: 'Redes Sociales',
    icon: FiShare2,
    descripcion: 'Si tu tarea/incidencia está relacionada con la dinamización de tus redes sociales.',
  },
];

const NuevoTicket = () => {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [cargandoHorario, setCargandoHorario] = useState(true);
  const [soporteDisponible, setSoporteDisponible] = useState(false);
  const [isDragging, setIsDragging] = useState(false);
  const [saliendo, setSaliendo] = useState(false);

  useEffect(() => {
    const calcularDisponibilidad = async () => {
      try {
        const resp = await axios.get(`${BASE_URL}/ajustes/horario`);
        const horario = resp.data?.ajuste?.valor;

        const ahora = new Date();
        const dia = ahora.getDay(); // 0=Dom … 6=Sáb
        const hora = ahora.getHours();
        const minutos = ahora.getMinutes();
        const horaDecimal = hora + minutos / 60;

        // Hora de inicio del sistema (fija: 8:00)
        const horaInicio = horario?.hora_inicio ?? 8;

        // Hora de cierre general y viernes desde ajustes
        const horaCierre = horario?.hora_cierre ?? 16;
        const horaCierreViernes = horario?.hora_cierre_viernes ?? 15;

        const esLaborable = dia >= 1 && dia <= 5; // Lun–Vie
        const esViernes = dia === 5;
        const cierreHoy = esViernes ? horaCierreViernes : horaCierre;

        setSoporteDisponible(esLaborable && horaDecimal >= horaInicio && horaDecimal < cierreHoy);
      } catch {
        // Si falla la petición, calcular con valores por defecto
        const ahora = new Date();
        const dia = ahora.getDay();
        const hora = ahora.getHours();
        const esLaborable = dia >= 1 && dia <= 5;
        const cierreHoy = dia === 5 ? 15 : 16;
        setSoporteDisponible(esLaborable && hora >= 8 && hora < cierreHoy);
      } finally {
        setCargandoHorario(false);
      }
    };

    calcularDisponibilidad();
  }, []);

  const navigate = useNavigate();
  const [content, setContent] = useState('');
  const [attachments, setAttachments] = useState([]);
  const [asunto, setAsunto] = useState('');
  const location = useLocation();
  const departmentFromState = location.state?.department || 'no_especificado'; // Recibir el departamento

  const [departamento, setDepartamento] = useState(departmentFromState); // Inicializar con el valor recibido

  const [prioridad, setPrioridad] = useState('no_urgente');
  const [todosUsuarios, setTodosUsuarios] = useState([]);
  const [usuarioDestinatario, setUsuarioDestinatario] = useState(null); // usuario al que se le abre el ticket
  const [busquedaDestinatario, setBusquedaDestinatario] = useState('');
  const [mostrarDropdownDestinatario, setMostrarDropdownDestinatario] = useState(false);
  const dropdownAsignacionRef = useRef(null);

  // Definición de tokenInfo
  const token = localStorage.getItem("AuthToken");
  let tokenInfo = {};
  if (token) {
    tokenInfo = jwtDecode(token);
  }

  // Cargar lista de usuarios (rol user) si el rol es admin o técnico
  useEffect(() => {
    if (tokenInfo.role === 'admin' || tokenInfo.role === 'tecnico') {
      axios.get(`${BASE_URL}/todos-usuarios`)
        .then(res => setTodosUsuarios((res.data.usuarios || []).filter(u => u.role === 'user')))
        .catch(err => console.error('Error al cargar usuarios:', err));
    }
  }, [tokenInfo.role]);

  // Cerrar dropdown al hacer click fuera
  useEffect(() => {
    const handleClickFuera = (e) => {
      if (dropdownAsignacionRef.current && !dropdownAsignacionRef.current.contains(e.target)) {
        setMostrarDropdownDestinatario(false);
      }
    };
    document.addEventListener('mousedown', handleClickFuera);
    return () => document.removeEventListener('mousedown', handleClickFuera);
  }, [])

  // Usuarios filtrados por búsqueda (solo rol user)
  const usuariosFiltrados = useMemo(() =>
    todosUsuarios.filter(u =>
      `${u.nombre} ${u.apellidos} ${u.usuario}`.toLowerCase().includes(busquedaDestinatario.toLowerCase())
    ), [todosUsuarios, busquedaDestinatario]);

  const seleccionarDestinatario = (usuario) => {
    setUsuarioDestinatario(usuario);
    setBusquedaDestinatario('');
    setMostrarDropdownDestinatario(false);
  };

  const limpiarDestinatario = () => {
    setUsuarioDestinatario(null);
    setBusquedaDestinatario('');
  };

  if (tokenInfo.estado === 'verificar') return <Verificacion />;
  if (tokenInfo.estado === 'suspendido') return <Suspendido />;
  if (cargandoHorario) return <div className="loader-fullscreen"><Loader /></div>;

  const handleChange = (value) => {
    setContent(value);
  };

  const procesarArchivos = (files) => {
    const maxFileSize = 5 * 1024 * 1024; // 5MB en bytes
    const validFiles = [];

    for (let i = 0; i < files.length; i++) {
      if (files[i].size <= maxFileSize) {
        validFiles.push(files[i]);
      } else {
        toast.error(`El archivo ${files[i].name} excede el tamaño máximo de 5MB.`);
      }
    }

    // Se acumulan: los archivos pueden llegar del dropzone, del selector o del editor
    setAttachments((previos) => {
      const actuales = Array.from(previos || []);
      const nuevos = validFiles.filter(
        (file) => !actuales.some((existente) => existente.name === file.name && existente.size === file.size)
      );
      return [...actuales, ...nuevos];
    });
  };

  const handleAttachmentChange = (e) => {
    procesarArchivos(e.target.files);
    e.target.value = ''; // Permite volver a elegir el mismo archivo tras quitarlo
  };

  const handleDropArchivos = (e) => {
    e.preventDefault();
    setIsDragging(false);
    if (e.dataTransfer.files?.length) {
      procesarArchivos(e.dataTransfer.files);
    }
  };

  const handleRemoveAttachment = (index) => {
    const updatedAttachments = [...attachments];
    updatedAttachments.splice(index, 1);
    setAttachments(updatedAttachments);
  };

  const handleAsuntoChange = (e) => {
    setAsunto(e.target.value);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    window.scrollTo({ top: 0, behavior: 'smooth' });

    if (asunto.trim().length < 5) {
        toast.error('El asunto no puede estar vacío y debe tener al menos 5 caracteres.');
        return;
    }

    // El editor devuelve HTML: se sanea y se mide el texto visible, no las etiquetas
    const descripcion = sanitizarHtml(content);
    const textoDescripcion = new DOMParser()
        .parseFromString(`<body>${descripcion}</body>`, 'text/html')
        .body.textContent.replace(/\u00A0/g, ' ').trim();

    if (esHtmlVacio(descripcion) || textoDescripcion.length < 5) {
        toast.error('La descripción no puede estar vacía y debe tener al menos 5 caracteres.');
        return;
    }

    setIsSubmitting(true);
      // Validación de selección de departamento
      if (departamento === 'no_especificado') {
        toast.error('Debes seleccionar un departamento.');
        setIsSubmitting(false);
        return;
      }

    const identificador_unico = uniqid.time();
    const uniqid_up = identificador_unico.toUpperCase();

    // Si se abre el ticket en nombre de otro usuario, usar sus datos
    const creador = usuarioDestinatario || tokenInfo;

    const formData = new FormData();
    formData.append('identificador', uniqid_up);
    formData.append('nombre', creador.nombre);
    formData.append('apellidos', creador.apellidos);
    formData.append('telefono', creador.telefono);
    formData.append('email', creador.email);
    formData.append('webs_del_cliente', creador.webs_del_cliente);
    formData.append('asunto', asunto);
    formData.append('descripcion', descripcion);
    formData.append('dpto', departamento);
    formData.append('usuario_creador', creador.usuario);
    formData.append('abierto_por', tokenInfo.usuario);
    formData.append('prioridad', prioridad);

    for (let i = 0; i < attachments.length; i++) {
        formData.append('archivos', attachments[i]);
    }

    try {
        // Crear el ticket
        const response = await axios.post(`${BASE_URL}/crear-ticket`, formData, {
            headers: {
                'Content-Type': 'multipart/form-data',
            }
        });
        window.scrollTo({ top: 0, behavior: 'smooth' });

        // Transición suave: se desvanece el formulario antes de navegar al ticket creado
        setSaliendo(true);
        await new Promise((resolve) => setTimeout(resolve, 280));
        navigate(`/ticket-creado/${response.data.identificador}`);

        // Llamar a la nueva ruta para notificar a administrador
        await axios.post(`${BASE_URL}/notificar-nuevo-ticket-admin`, {
            asunto: asunto,
            identificador: uniqid_up,
            usuario_creador: creador.usuario,
            descripcion: descripcion,
            nombre: creador.nombre,
            apellidos: creador.apellidos,
            dpto: departamento,
            // Si es un técnico abriendo en nombre de un usuario, se indica para usar plantilla distinta
            ...(usuarioDestinatario && {
                nombre_tecnico: tokenInfo.nombre,
                usuario_tecnico: tokenInfo.usuario
            })
        });

        await axios.post(`${BASE_URL}/notificar-nuevo-ticket-cliente`, {
          asunto: asunto,
          identificador: uniqid_up,
          usuario_creador: creador.usuario,
          descripcion: descripcion,
          email_cliente: creador.email,
          nombre_cliente: creador.nombre
      });

        // Si el ticket fue abierto en nombre de un usuario por un técnico/admin, notificar al destinatario
        if (usuarioDestinatario) {
          await axios.post(`${BASE_URL}/notificar-ticket-gestion`, {
            identificador: uniqid_up,
            asunto: asunto,
            descripcion: descripcion,
            dpto: departamento,
            nombre_destinatario: usuarioDestinatario.nombre,
            email_destinatario: usuarioDestinatario.email,
            nombre_tecnico: tokenInfo.nombre,
            usuario_tecnico: tokenInfo.usuario
          });
        }

    } catch (error) {
        toast.error('Error al crear el ticket. Inténtalo de nuevo más tarde.');
        console.error('Error al crear el ticket:', error);
        setIsSubmitting(false); // Desbloquear el botón si hay error
    }
  };

  const esStaff = tokenInfo.role === 'admin' || tokenInfo.role === 'tecnico';
  const departamentoSeleccionado = DEPARTAMENTOS.find(d => d.value === departamento);

  return (
    <main className={`nuevo-ticket site-width ${saliendo ? 'nt-saliendo' : ''}`}>
      <div className="nt-layout">
        <aside className="nt-aside">
          <div className="nt-card nt-welcome-card">
            <h2>👋🏻 ¡Hola {tokenInfo.nombre}!</h2>
            {esStaff ? (
              <p>Estás creando un ticket como <strong>{tokenInfo.role === 'admin' ? 'administrador' : 'técnico'}</strong>. Puedes asignarlo directamente a cualquier usuario.</p>
            ) : (
              <p>Rellena el siguiente formulario y te asignaremos a un técnico para que gestione tu tarea/incidencia lo antes posible a través de un chat en tiempo real.</p>
            )}
          </div>

          <div className={`nt-availability-pill ${soporteDisponible ? 'is-open' : 'is-closed'}`}>
            <span className="nt-availability-dot" />
            {soporteDisponible ? 'Soporte disponible ahora' : 'Fuera de horario laboral'}
          </div>
          {!soporteDisponible && (
            <p className="nt-availability-note">
              {esStaff
                ? 'El ticket será gestionado por orden de llegada en el próximo horario hábil.'
                : 'Nuestros técnicos no están disponibles ahora mismo. Al crear el ticket se responderá dentro de horario por orden de llegada.'}
            </p>
          )}

          <div className="nt-card nt-contact-card">
            <div className="nt-card-header">
              <span>Tus datos de contacto</span>
              <Link to="/dashboard/editar-perfil" className="nt-edit-link">Editar</Link>
            </div>
            <p className="nt-card-hint">
              {esStaff
                ? 'El ticket quedará registrado con tu cuenta.'
                : 'Los usaremos para notificarte y contactarte si es necesario.'}
            </p>
            <div className="nt-contact-list">
              <div className="nt-contact-row">
                <FiUser />
                <div><span>Nombre</span><p>{tokenInfo.nombre} {tokenInfo.apellidos}</p></div>
              </div>
              <div className="nt-contact-row">
                <FiPhone />
                <div><span>Teléfono</span><p>{tokenInfo.telefono || '—'}</p></div>
              </div>
              <div className="nt-contact-row">
                <FiMail />
                <div><span>Email</span><p>{tokenInfo.email}</p></div>
              </div>
              <div className="nt-contact-row">
                <FiGlobe />
                <div><span>Sitios en mantenimiento</span><p>{tokenInfo.webs_del_cliente || '—'}</p></div>
              </div>
            </div>
          </div>
        </aside>

        <section className="nt-card nt-form-card">
          <h3>{esStaff ? 'Crear nuevo ticket' : '¡Estás a punto de crear un ticket!'}</h3>

          {esStaff && (
            <div className="nt-section nt-assign-block">
              <label className="nt-label">Abrir ticket a un usuario <span className='nt-optional'>(Opcional)</span></label>

              {usuarioDestinatario ? (
                <div className="asignacion-chip">
                  <div className="asignacion-chip-info">
                    <span className="asignacion-chip-nombre">{usuarioDestinatario.nombre} {usuarioDestinatario.apellidos}</span>
                    <span className="asignacion-chip-usuario">@{usuarioDestinatario.usuario}</span>
                    <span className="asignacion-chip-email">{usuarioDestinatario.email}</span>
                  </div>
                  <button type="button" className="asignacion-chip-quitar" onClick={limpiarDestinatario} title="Quitar">&#10005;</button>
                </div>
              ) : (
                <div className="selector-usuario-busqueda" ref={dropdownAsignacionRef}>
                  <div className="campo-busqueda-usuario">
                    <input
                      type="text"
                      className="input-busqueda-usuario"
                      placeholder="Busca por nombre, apellidos o @usuario..."
                      value={busquedaDestinatario}
                      onChange={(e) => setBusquedaDestinatario(e.target.value)}
                      onFocus={() => setMostrarDropdownDestinatario(true)}
                    />
                    {busquedaDestinatario && (
                      <button type="button" className="boton-limpiar-usuario" onClick={() => setBusquedaDestinatario('')}>&#10005;</button>
                    )}
                  </div>

                  {mostrarDropdownDestinatario && (
                    <div className="lista-usuarios-desplegable">
                      {usuariosFiltrados.length > 0 ? (
                        usuariosFiltrados.map(u => (
                          <div key={u.usuario} className="item-usuario-desplegable" onClick={() => seleccionarDestinatario(u)}>
                            <div className="info-usuario-item">
                              <span className="nombre-usuario-item">{u.nombre} {u.apellidos}</span>
                              <span className="usuario-item">@{u.usuario}</span>
                            </div>
                            <span className="email-usuario-item">{u.email}</span>
                          </div>
                        ))
                      ) : (
                        <div className="sin-resultados-usuario">No se encontraron usuarios</div>
                      )}
                    </div>
                  )}
                </div>
              )}
            </div>
          )}

          <div className="nt-section">
            <label className="nt-label">¿Qué departamento se encargará de tu tarea/incidencia? <span className='nt-required'>*</span></label>
            <div className="nt-dept-grid">
              {DEPARTAMENTOS.map((dept) => (
                <button
                  key={dept.value}
                  type="button"
                  className={`nt-dept-card nt-dept-card--${dept.value} ${departamento === dept.value ? 'active' : ''}`}
                  aria-pressed={departamento === dept.value}
                  onClick={() => setDepartamento(dept.value)}
                >
                  <dept.icon />
                  <span>{dept.label}</span>
                </button>
              ))}
            </div>
            {departamentoSeleccionado && (
              <p className={`nt-dept-desc nt-dept-desc--${departamentoSeleccionado.value}`}>{departamentoSeleccionado.descripcion}</p>
            )}
          </div>

          <div className="nt-section">
            <label className="nt-label" htmlFor="nt-asunto">Asunto <span className='nt-required'>*</span></label>
            <input id="nt-asunto" className="nt-input" type="text" placeholder='Introduzca un título a tu tarea/incidencia...' value={asunto} onChange={handleAsuntoChange} />
          </div>

          <div className="nt-section">
            <label className="nt-label">Describa detalladamente tu tarea/incidencia <span className='nt-required'>*</span></label>
            <RichTextEditor
              className="nt-rte"
              value={content}
              onChange={handleChange}
              onFiles={procesarArchivos}
              placeholder="Describe aquí tu tarea/incidencia…"
            />
          </div>

          <div className="nt-section">
            <label className="nt-label">Adjuntar archivos <span className='nt-optional'>(Opcional)</span></label>
            <p className="nt-hint">También puedes arrastrarlos o pegarlos directamente en la descripción. Si algún archivo pesa más de 5 MB, súbelo a <a href='https://wetransfer.com/' target='_blank' rel="noreferrer">WeTransfer</a> y pega el enlace en la descripción.</p>
            <label
              htmlFor="nt-file-input"
              className={`nt-dropzone ${isDragging ? 'is-dragging' : ''}`}
              onDragOver={(e) => { e.preventDefault(); setIsDragging(true); }}
              onDragLeave={() => setIsDragging(false)}
              onDrop={handleDropArchivos}
            >
              <FiUploadCloud />
              <p>Arrastra tus archivos o haz clic para buscarlos</p>
              <small>PDF, Word, Pages, HTML e imágenes (JPG, PNG, WEBP) · Máx. 5 MB por archivo</small>
              <input
                className="nt-dropzone-input"
                accept=".pdf,.doc,.docx,.jpg,.png,.jpeg,.webp,.pages,.html"
                name="text"
                id="nt-file-input"
                type="file"
                multiple
                onChange={handleAttachmentChange}
              />
            </label>
            {attachments && attachments.length > 0 && (
                <div className='nt-files-grid'>
                  {Array.from(attachments).map((file, index) => (
                    <div className='nt-file-chip' key={index}>
                      <FiFile />
                      <p>{file.name}</p>
                      <button type="button" className='nt-file-remove' onClick={() => handleRemoveAttachment(index)}>&#10005;</button>
                    </div>
                  ))}
                </div>
            )}
          </div>

          <div className="nt-section">
            <label className="nt-label">¿Es urgente?</label>
            <div className="nt-priority-toggle">
              <button
                type="button"
                className={prioridad === 'no_urgente' ? 'active' : ''}
                onClick={() => setPrioridad('no_urgente')}
              >
                No urgente
              </button>
              <button
                type="button"
                className={`nt-priority--urgente ${prioridad === 'urgente' ? 'active' : ''}`}
                onClick={() => setPrioridad('urgente')}
              >
                Urgente
              </button>
            </div>
          </div>

          <button
            className='nt-submit-btn'
            onClick={handleSubmit}
            disabled={isSubmitting}
          >
            {isSubmitting ? (
              <><span className="btn-spinner" /> Enviando...</>
            ) : (
              <>Enviar ticket <FiArrowRight /></>
            )}
          </button>
        </section>
      </div>
      <ToastContainer position="bottom-right" autoClose={3000} />
    </main>
  )
}

export default NuevoTicket;
